import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Typography,
  IconButton,
  Button,
  useTheme,
  Box,
  MenuItem,
  Paper,
  Tooltip,
  CircularProgress,
  Menu,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as LoadTemplateIcon,
  PictureAsPdf as PdfIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import RichTextEditor from './RichTextEditor';
import SaveTemplateDialog from './SaveTemplateDialog';
import {
  SaveTemplateRequest,
  TemplateType,
  Template,
  STARTER_TEMPLATES,
} from '../../types/template.types';
import { TemplateVariables } from '../../types/template.types';

interface MaximizedTextEditorProps {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  title: string;
  placeholder?: string;
  doneButtonText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  maxHeight?: string;
  enableTemplates?: boolean;
  suggestedTemplateType?: TemplateType;
  onSaveTemplate?: (templateData: SaveTemplateRequest) => Promise<void>;
  userTemplates?: Template[];
  onSave?: (value: string) => void;
  isLoading?: boolean;
  loadingMessage?: string;
  templateVariables?: TemplateVariables;
  onTemplateLoad?: (templateId: string) => void;
  isStreaming?: boolean;
  streamingMessage?: string;
  enableWorkDescriptionTemplate?: boolean; // New prop to control default template
}

const MaximizedTextEditor: React.FC<MaximizedTextEditorProps> = ({
  open,
  onClose,
  value,
  onChange,
  title,
  placeholder = '',
  doneButtonText = 'Done',
  maxWidth = false,
  maxHeight = '90vh',
  enableTemplates = false,
  suggestedTemplateType = 'general',
  onSaveTemplate,
  userTemplates = [],
  onSave,
  isLoading = false,
  loadingMessage = 'Please wait while we generate your report content...',
  templateVariables = {},
  onTemplateLoad,
  isStreaming = false,
  streamingMessage = 'Generating content...',
  enableWorkDescriptionTemplate = false,
}) => {
  const theme = useTheme();
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  // const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [currentValue, setCurrentValue] = useState(value);
  const [isExporting, setIsExporting] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [customMenuAnchor, setCustomMenuAnchor] = useState<null | HTMLElement>(null);
  const [isHoverDisabled, setIsHoverDisabled] = useState(false);
  const [contentBeforeTemplate, setContentBeforeTemplate] = useState<string>('');

  // Update currentValue when external value changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value, open]);

  // Save original content snapshot when dialog opens
  useEffect(() => {
    if (open) {
      setCanUndo(false);
      setIsPreviewMode(false);
      setContentBeforeTemplate('');
    }
  }, [open, value]);

  const handleChange = useCallback(
    (newValue: string) => {
      setCurrentValue(newValue);
      onChange(newValue);
    },
    [onChange]
  );

  // Memoize template arrays to prevent unnecessary re-renders
  const allTemplates = useMemo(() => {
    const filteredStarterTemplates = STARTER_TEMPLATES.filter(
      template => template.templateType === suggestedTemplateType
    );
    const filteredUserTemplates = userTemplates.filter(
      template => template.templateType === suggestedTemplateType
    );
    return [...filteredStarterTemplates, ...filteredUserTemplates];
  }, [userTemplates, suggestedTemplateType]);

  // Handle template preview on hover
  const handleTemplateHover = useCallback(
    (templateId: string) => {
      if (isHoverDisabled) return;
      const template = allTemplates.find(t => t.id === templateId);
      if (template && !isPreviewMode) {
        setIsPreviewMode(true);

        // Process the template with variables for preview
        let processedTemplate = template.templateText;
        Object.entries(templateVariables || {}).forEach(([key, value]) => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          processedTemplate = processedTemplate.replace(regex, value || `[${key} not found]`);
        });

        // Only update the display, not the actual editor content
        setCurrentValue(processedTemplate);
      }
    },
    [allTemplates, isPreviewMode, templateVariables, isHoverDisabled]
  );

  // Reset preview when mouse leaves
  const handleTemplateHoverEnd = useCallback(() => {
    if (isHoverDisabled) return;
    if (isPreviewMode) {
      setIsPreviewMode(false);
      setCurrentValue(value); // Restore to the actual value prop
    }
  }, [isPreviewMode, value, isHoverDisabled]);

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      if (!templateId) return;

      setIsHoverDisabled(true);
      setTimeout(() => setIsHoverDisabled(false), 500);

      const selectedTemplate = allTemplates.find(t => t.id === templateId);
      if (!selectedTemplate) return;

      // Store current content for undo BEFORE making any changes
      setContentBeforeTemplate(value);
      setIsPreviewMode(false);

      if (onTemplateLoad) {
        onTemplateLoad(templateId);
      }

      // Process the template with provided variables
      let processedTemplate = selectedTemplate.templateText;
      Object.entries(templateVariables || {}).forEach(([key, templateValue]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, templateValue || `[${key} not found]`);
      });

      let newContent: string;
      if (value.trim().length === 0) {
        newContent = processedTemplate;
      } else {
        const separator = '<hr style="margin: 20px 0; border: 1px solid #ddd;" />';
        newContent = value + separator + processedTemplate;
      }

      // Update the editor content and enable undo
      setCurrentValue(newContent);
      onChange(newContent);
      setCanUndo(true);
      setCustomMenuAnchor(null);
    },
    [allTemplates, value, onChange, templateVariables, onTemplateLoad]
  );

  const handleUndo = useCallback(() => {
    if (canUndo && contentBeforeTemplate !== '') {
      // Restore to the content before template insertion
      setCurrentValue(contentBeforeTemplate);
      onChange(contentBeforeTemplate);
      setCanUndo(false);
      setContentBeforeTemplate('');
    }
  }, [canUndo, contentBeforeTemplate, onChange]);

  // Reset states when opening the menu
  const handleOpenMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setCustomMenuAnchor(e.currentTarget);
      setIsHoverDisabled(false);
      setIsPreviewMode(false);
      // Restore to actual value if we were in preview mode
      setCurrentValue(value);
    },
    [value]
  );

  const handleSaveTemplate = useCallback(
    async (templateData: SaveTemplateRequest) => {
      try {
        if (onSaveTemplate) {
          await onSaveTemplate(templateData);
        }
        setSaveTemplateDialogOpen(false);
      } catch (error) {
        console.error('Error saving template:', error);
      }
    },
    [onSaveTemplate]
  );

  const handleDoneClick = useCallback(() => {
    if (onSave) {
      onSave(currentValue);
    } else {
      onClose();
    }
  }, [onSave, currentValue, onClose]);

  const handleExportToPDF = useCallback(async () => {
    try {
      setIsExporting(true);

      // Find the Quill editor content
      const editorContent = document.querySelector('.ql-editor') as HTMLElement;
      if (!editorContent) {
        throw new Error('Could not find editor content');
      }

      // Create a complete HTML document for printing
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${title}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Helvetica', 'Arial', sans-serif;
              font-size: 12pt;
              line-height: 1.6;
              color: #000;
              margin: 0;
              padding: 0;
              background: white;
            }
            
            h1 {
              font-size: 24pt;
              font-weight: bold;
              margin: 18pt 0 12pt 0;
              page-break-after: avoid;
            }
            
            h2 {
              font-size: 20pt;
              font-weight: bold;
              margin: 16pt 0 10pt 0;
              page-break-after: avoid;
            }
            
            h3 {
              font-size: 16pt;
              font-weight: bold;
              margin: 14pt 0 8pt 0;
              page-break-after: avoid;
            }
            
            h4 {
              font-size: 14pt;
              font-weight: bold;
              margin: 12pt 0 6pt 0;
              page-break-after: avoid;
            }
            
            h5, h6 {
              font-size: 12pt;
              font-weight: bold;
              margin: 10pt 0 6pt 0;
              page-break-after: avoid;
            }
            
            p {
              margin: 6pt 0;
              page-break-inside: avoid;
            }
            
            ul, ol {
              margin: 6pt 0;
              padding-left: 20pt;
            }
            
            li {
              margin: 3pt 0;
              page-break-inside: avoid;
            }
            
            strong {
              font-weight: bold;
            }
            
            em {
              font-style: italic;
            }
            
            u {
              text-decoration: underline;
            }
            
            .ql-align-center {
              text-align: center;
            }
            
            .ql-align-right {
              text-align: right;
            }
            
            .ql-align-justify {
              text-align: justify;
            }
            
            .ql-indent-1 { margin-left: 30pt; }
            .ql-indent-2 { margin-left: 60pt; }
            .ql-indent-3 { margin-left: 90pt; }
            .ql-indent-4 { margin-left: 120pt; }
            .ql-indent-5 { margin-left: 150pt; }
            .ql-indent-6 { margin-left: 180pt; }
            .ql-indent-7 { margin-left: 210pt; }
            .ql-indent-8 { margin-left: 240pt; }
            
            blockquote {
              border-left: 3pt solid #ccc;
              margin: 12pt 0;
              padding-left: 12pt;
              font-style: italic;
            }
            
            hr {
              border: none;
              border-top: 1pt solid #ccc;
              margin: 12pt 0;
            }
            
            /* Hide Quill UI elements */
            .ql-ui {
              display: none !important;
            }
            
            /* Ensure proper list styling */
            ol[data-list="bullet"] {
              list-style-type: disc;
            }
            
            ol[data-list="ordered"] {
              list-style-type: decimal;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${editorContent.innerHTML}
        </body>
        </html>
      `;

      // Open a new window with the content
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Could not open print window. Please allow popups for this site.');
      }

      // Write the content to the new window
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for the content to load
      printWindow.onload = () => {
        // Focus the window and trigger print
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          // Close the window after printing (optional)
          setTimeout(() => {
            printWindow.close();
          }, 1000);
        }, 500);
      };

      // If onload doesn't fire, try after a delay
      setTimeout(() => {
        if (printWindow && !printWindow.closed) {
          printWindow.focus();
          printWindow.print();
        }
      }, 1000);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      if (error instanceof Error) {
        alert(`Failed to export PDF: ${error.message}`);
      } else {
        alert('Failed to export PDF. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  }, [title]);

  return (
    <>
      {open &&
        ReactDOM.createPortal(
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999, // Very high z-index to ensure it's on top
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onKeyDown={e => {
              e.stopPropagation();
            }}
          >
            <Paper
              elevation={24}
              sx={{
                height: maxHeight,
                maxHeight: maxHeight,
                width: '90vw',
                maxWidth: maxWidth ? theme.breakpoints.values[maxWidth] : '90vw',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                outline: 'none',
                backgroundColor: theme.palette.background.paper,
              }}
              onKeyDown={e => {
                e.stopPropagation();
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  flexShrink: 0, // Prevent header from shrinking
                }}
                onKeyDown={e => {
                  e.stopPropagation();
                }}
              >
                <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
                  {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {enableTemplates && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleOpenMenu}
                        sx={{
                          minWidth: 160,
                          textTransform: 'none',
                        }}
                        startIcon={<LoadTemplateIcon />}
                        disabled={allTemplates.length === 0}
                      >
                        Load Template
                      </Button>

                      <Menu
                        anchorEl={customMenuAnchor}
                        open={Boolean(customMenuAnchor)}
                        onClose={() => {
                          setCustomMenuAnchor(null);
                          handleTemplateHoverEnd();
                        }}
                        PaperProps={{
                          sx: {
                            maxHeight: 300,
                            zIndex: 999999,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                          },
                        }}
                        MenuListProps={{
                          sx: {
                            zIndex: 999999,
                          },
                        }}
                        sx={{
                          zIndex: 999999,
                        }}
                      >
                        {/* Dynamic Templates */}
                        {allTemplates.map(template => (
                          <MenuItem
                            key={template.id}
                            onClick={() => {
                              if (template.id) {
                                handleTemplateSelect(template.id);
                              }
                              setCustomMenuAnchor(null);
                            }}
                            onMouseEnter={() => {
                              if (template.id) {
                                handleTemplateHover(template.id);
                              }
                            }}
                            onMouseLeave={handleTemplateHoverEnd}
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              margin: '2px',
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                borderColor: theme.palette.primary.main,
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {template.templateName}
                              </Typography>
                              {template.description && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', display: 'block' }}
                                >
                                  {template.description}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                  <span>
                    <Tooltip title="Undo Template">
                      <span>
                        <IconButton
                          onClick={handleUndo}
                          size="small"
                          disabled={!canUndo}
                          sx={{
                            color: canUndo
                              ? theme.palette.primary.main
                              : theme.palette.action.disabled,
                          }}
                        >
                          <UndoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </span>
                  <span>
                    <Tooltip title="Download as PDF">
                      <span>
                        <IconButton
                          onClick={handleExportToPDF}
                          size="small"
                          disabled={isExporting || !currentValue.trim()}
                          sx={{
                            color: currentValue.trim()
                              ? theme.palette.primary.main
                              : theme.palette.action.disabled,
                          }}
                        >
                          <PdfIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </span>
                  <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Content */}
              <Box
                sx={{
                  p: 2,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
                onKeyDown={e => {
                  e.stopPropagation();
                }}
              >
                {/* Show loading overlay when isLoading prop is true */}
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="h6" color="primary">
                      Generating Report
                    </Typography>
                    <Typography variant="body2" color="textSecondary" textAlign="center">
                      {loadingMessage}
                    </Typography>
                  </Box>
                )}

                {/* Show streaming indicator when content is being streamed */}
                {isStreaming && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      zIndex: 1001,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <CircularProgress size={16} thickness={4} sx={{ color: 'inherit' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {streamingMessage}
                    </Typography>
                  </Box>
                )}
                <RichTextEditor
                  value={currentValue}
                  onChange={handleChange}
                  placeholder={placeholder}
                  height="100%"
                  enableWorkDescriptionTemplate={enableWorkDescriptionTemplate}
                />
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  flexShrink: 0, // Prevent footer from shrinking
                }}
              >
                <Box></Box>
                <Button onClick={handleDoneClick} variant="contained">
                  {doneButtonText}
                </Button>
              </Box>
            </Paper>
          </Box>,
          document.body
        )}

      {enableTemplates && (
        <SaveTemplateDialog
          open={saveTemplateDialogOpen}
          onClose={() => setSaveTemplateDialogOpen(false)}
          onSave={handleSaveTemplate}
          currentText={value}
          suggestedType={suggestedTemplateType}
        />
      )}
    </>
  );
};

export default MaximizedTextEditor;
